"use client"
import { useTheme, View } from '@aws-amplify/ui-react';
import { AiOutlineMenu } from "react-icons/ai";
import { Flex, Button, Image, Menu, MenuItem, MenuButton} from '@aws-amplify/ui-react';
import { useRouter, usePathname } from 'next/navigation'
import {useState, useTransition, useEffect} from 'react'
import { fetchUserAttributes } from 'aws-amplify/auth';
import { globalCustomVariables } from '../global_variables/global';
import { MdAccountCircle } from "react-icons/md";
import { IoIosSearch } from 'react-icons/io';
import { getDynamicAuthMode } from '@/utils/getDynamicAuthMode';
import { GUEST_INTEREST_COOKIE, parseGuestInterest } from '@/lib/guest-interest';
import { slugifyTopic } from '@/utils/slugify';

import LoadingComponent from "@/components/loading_component/loading_component";

function getSavedGuestInterest(): string | null {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${GUEST_INTEREST_COOKIE}=`));

  return cookie
    ? parseGuestInterest(cookie.slice(cookie.indexOf('=') + 1))
    : null;
}

export default function ModNavHeader() {
  // Resolve viewport on the client to avoid rendering the wrong nav layout first.
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [isCompactDesktop, setIsCompactDesktop] = useState<boolean | null>(null);
  const logoHeight = '45px';

  // This is for the loading animaition component
  const loading_wheel_color = "brand.primary"

  const router = useRouter()
  const pathname = usePathname() // ← Added usePathname for App Router
  
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const {tokens} = useTheme()

  const brand = tokens.colors.brand as any
  const normalizeLabel = (label: string) => label.replace(/\s+/g, '').toLowerCase()

  const [isLoading, startTransition] = useTransition()

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 991px)')
    const compactDesktopQuery = window.matchMedia(
      '(min-width: 992px) and (max-width: 1199px)'
    )

    const updateViewport = () => {
      setIsMobile(mobileQuery.matches)
      setIsCompactDesktop(compactDesktopQuery.matches)
    }

    updateViewport()

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateViewport)
      compactDesktopQuery.addEventListener('change', updateViewport)
      return () => {
        mobileQuery.removeEventListener('change', updateViewport)
        compactDesktopQuery.removeEventListener('change', updateViewport)
      }
    }

    mobileQuery.addListener(updateViewport)
    compactDesktopQuery.addListener(updateViewport)
    return () => {
      mobileQuery.removeListener(updateViewport)
      compactDesktopQuery.removeListener(updateViewport)
    }
  }, [])

  // whenever the route changes, update activeItem
  useEffect(() => {
    // keep the normalized route segment so it can be compared against menu labels
    const path = pathname === '/' ? 'home' : pathname.replace(/^\//, '').split('/')[0]
    setActiveItem(path.toLowerCase())
  }, [pathname]) // ← Changed from router.pathname to pathname

  const handleNavHeaderButtonClick = async (label: string) => {
    // reset your global token
    globalCustomVariables.next_token_value = null

    // figure out the URL
    let href = label === 'home'
      ? '/'
      : `/${label.replace(/\s+/g, '-').toLowerCase()}`

    if (label === 'issues') {
      const fallbackTopic = 'Economy and Jobs';
      const authMode = await getDynamicAuthMode();
      let defaultTopic = fallbackTopic;

      if (authMode === 'userPool') {
        try {
          const attributes = await fetchUserAttributes();
          defaultTopic = attributes['custom:LegislationInterests']
            ?.split(',')
            .map((interest) => interest.trim())
            .find(Boolean) ?? fallbackTopic;
        } catch (error) {
          console.error('Unable to load saved user interests:', error);
        }
      } else {
        defaultTopic = getSavedGuestInterest() ?? fallbackTopic;
      }

      href = `/issues/${slugifyTopic(defaultTopic)}`;
    }

    // navigate
    startTransition(() => {
      router.push(href)
    })
  }

  const isViewportReady = isMobile !== null && isCompactDesktop !== null
  const showMobileLayout = isMobile === true

  return (
    <>
      <View
        className="mp-site-header"
        style={{
          flexShrink: 0,
          opacity: isViewportReady ? 1 : 0,
          transition: 'opacity 120ms ease-out',
        }}
      >
        {/* Background (translucent with blur where supported) and positioning (fixed on mobile) come from .mp-site-header__bar in modern.css */}
        <View
          className="mp-site-header__bar"
          width="100%"
          height="60px"
          style={{
            borderBottom: "1px solid transparent",
            borderImage: `linear-gradient(90deg, ${brand.primary.value}, ${brand.custom_purple.value}) 1`,
            boxShadow: "0 2px 14px rgba(0, 0, 0, 0.22)",
          }}
        >
          {/* MAIN HEADER ROW: [Left Home] [Center Logo (mobile only)] [Right Nav/Menu] */}
          <Flex
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            height="60px"
            width="100%"
            paddingLeft="8.5px"
            paddingRight="10px"
          >
            {/* LEFT: HOME BUTTON (icon on mobile, full logo on desktop) */}
            <Button
              onClick={() => handleNavHeaderButtonClick("home")}
              width={showMobileLayout ? "50px" : "auto"}
              height="50px"
              shrink="0"
              isDisabled={false}
              overflow="hidden"
              borderRadius={showMobileLayout ? "50%" : "0.5rem"}
              borderColor="transparent"
              backgroundColor="transparent"
              style={{
                color: "brand.nav_header",
                padding: showMobileLayout ? "0" : "0 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!isViewportReady ? null : showMobileLayout ? (
                <Image
                  src="/assets/moderateicon.png"
                  width="50px"
                  height="50px"
                  borderRadius="25px"
                  objectFit="cover"
                  alt="moderate politicians icon"
                />
              ) : (
                <Image
                  src="/assets/moderatepoliticians_white_its_mod-er-rate.png"
                  alt="Moderate Politicians logo"
                  width={isCompactDesktop ? "190px" : "250px"}
                  height="auto"
                  objectFit="contain"
                />
              )}
            </Button>

            {/* CENTER: LOGO (ONLY ON MOBILE) */}
            {isViewportReady && showMobileLayout && (
              <Flex
                flex="1"
                justifyContent="center"
                alignItems="center"
                onClick={() => handleNavHeaderButtonClick("home")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleNavHeaderButtonClick("home");
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Go to home"
                style={{ pointerEvents: "auto", cursor: "pointer" }}
              >
                <Image
                  src="/assets/moderatepoliticians_white_its_mod-er-rate.png"
                  alt="Moderate Politicians logo"
                  width="auto"
                  height={logoHeight as any}   // your useBreakpointValue height
                  maxWidth="60%"
                  objectFit="contain"
                />
              </Flex>
            )}


            {/* RIGHT: DESKTOP NAV OR MOBILE MENU */}
            {isViewportReady && (!showMobileLayout ? (
              // DESKTOP NAV BUTTONS
              <Flex
                direction="row"
                gap={isCompactDesktop ? "0.4rem" : "0.75rem"}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Flex
                  direction="row"
                  gap={isCompactDesktop ? "0.1rem" : "0.25rem"}
                  alignItems="center"
                  padding="0.25rem"
                  style={{
                    borderRadius: "2rem",
                    backgroundColor: "rgba(255, 255, 255, 0.07)",
                    border: "1px solid rgba(255, 255, 255, 0.09)",
                  }}
                >
                  {["Issues", "Daily Update", "Trending", "Politicians", "Method", "About"].map(
                    (label) => {
                      const key = normalizeLabel(label)
                      const isActive = activeItem === key
                      const isHovered = hoveredItem === key

                      return (
                        <Button
                          key={label}
                          onClick={() => handleNavHeaderButtonClick(key)}
                          onMouseEnter={() => setHoveredItem(key)}
                          onMouseLeave={() => setHoveredItem(null)}
                          minWidth={isCompactDesktop ? "74px" : "92px"}
                          height="38px"
                          whiteSpace="nowrap"
                          textAlign="center"
                          isDisabled={false}
                          variation="primary"
                          style={{
                            borderRadius: "2rem",
                            border: "none",
                            fontWeight: isActive ? 700 : 500,
                            fontSize: isCompactDesktop ? "0.82rem" : "1rem",
                            padding: isCompactDesktop ? "0 0.35rem" : undefined,
                            letterSpacing: "0.01em",
                            color: brand.white.value,
                            background: isActive
                              ? brand.primary.value
                              : isHovered
                              ? brand.primary.value
                              : "transparent",
                            boxShadow: isActive
                              ? `0 4px 14px ${brand.primary.value}55`
                              : isHovered
                              ? `0 4px 14px ${brand.primary.value}55`
                              : "none",
                            transform: isHovered && !isActive ? "translateY(-1px)" : "none",
                            transition:
                              "background 160ms ease-out, box-shadow 160ms ease-out, transform 160ms ease-out",
                          }}
                        >
                          {label}
                        </Button>
                      )
                    }
                  )}
                </Flex>

                <Button
                  onClick={() => handleNavHeaderButtonClick("search")}
                  onMouseEnter={() => setHoveredItem("search")}
                  onMouseLeave={() => setHoveredItem(null)}
                  variation="primary"
                  width="42px"
                  height="42px"
                  padding="0"
                  minWidth="auto"
                  aria-label="Search"
                  style={{
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${
                      hoveredItem === "search"
                        ? brand.primary.value
                        : "rgba(255, 255, 255, 0.25)"
                    }`,
                    background:
                      hoveredItem === "search"
                        ? brand.primary.value
                        : "rgba(255, 255, 255, 0.07)",
                    boxShadow:
                      hoveredItem === "search"
                        ? `0 4px 14px ${brand.primary.value}55`
                        : "none",
                    transition: "background 160ms ease-out, box-shadow 160ms ease-out",
                  }}
                >
                  <IoIosSearch size={24} color={brand.white.value} />
                </Button>

                <Button
                  onClick={() => handleNavHeaderButtonClick("account")}
                  onMouseEnter={() => setHoveredItem("account")}
                  onMouseLeave={() => setHoveredItem(null)}
                  variation="primary"
                  width="44px"
                  height="44px"
                  padding="0"
                  minWidth="auto"
                  aria-label="Account"
                  style={{
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    background:
                      activeItem === "account" || hoveredItem === "account"
                        ? brand.primary.value
                        : "transparent",
                    boxShadow:
                      activeItem === "account" || hoveredItem === "account"
                        ? `0 4px 14px ${brand.primary.value}55`
                        : "none",
                    transition: "background 160ms ease-out, box-shadow 160ms ease-out",
                  }}
                >
                  <MdAccountCircle size={38} color={brand.white.value} />
                </Button>
              </Flex>
            ) : (
              // MOBILE: HAMBURGER MENU
              <Flex
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                wrap="nowrap"
                gap="0.5rem"
                style={{ paddingTop: "5px" }}
              >
                <Menu
                  trigger={
                    <MenuButton
                      width="46px"
                      height="46px"
                      padding="0"
                      color={brand.white.value}
                      backgroundColor={brand.primary.value}
                      borderColor={brand.primary.value}
                      borderRadius="23px"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 10px ${brand.primary.value}55`,
                      }}
                    >
                      <AiOutlineMenu size="1.4em" color={brand.white.value} />
                    </MenuButton>
                  }
                  menuAlign="end"
                  size="large"
                  backgroundColor={brand.card_background.value}
                  borderRadius="1rem"
                  padding="0.5rem"
                  width="14rem"
                  style={{
                    border: `1px solid ${brand.custom_purple_shade.value}`,
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.18)",
                    overflow: "hidden",
                  }}
                >
                  {[
                    "Home",
                    "Issues",
                    "Daily Update",
                    "Politicians",
                    "Trending",
                    "Search",
                    "Method",
                    "About",
                    "Account",
                  ].map((label) => {
                    const key = normalizeLabel(label)
                    const isActive = activeItem === key
                    const isHovered = hoveredItem === key

                    return (
                      <MenuItem
                        key={label}
                        onMouseEnter={() => setHoveredItem(key)}
                        onMouseLeave={() => setHoveredItem(null)}
                        style={{
                          minHeight: "3rem",
                          borderRadius: "0.75rem",
                          marginBottom: "0.15rem",
                          paddingLeft: "0.9rem",
                          paddingRight: "0.9rem",
                          justifyContent: "flex-start",
                          fontSize: "1rem",
                          fontWeight: isActive ? 700 : 500,
                          borderLeft: `3px solid ${
                            isActive ? brand.primary.value : "transparent"
                          }`,
                          backgroundColor: isActive
                            ? brand.light_primary_green.value
                            : isHovered
                            ? brand.custom_purple_shade.value
                            : "transparent",
                          color: isActive
                            ? brand.primary.value
                            : isHovered
                            ? brand.custom_purple.value
                            : tokens.colors.font.primary.value,
                          transition:
                            "background-color 140ms ease-out, color 140ms ease-out",
                        }}
                        onClick={() => handleNavHeaderButtonClick(key)}
                      >
                        {label}
                      </MenuItem>
                    )
                  })}
                </Menu>
              </Flex>
            ))}
          </Flex>
        </View>

        {isLoading && <LoadingComponent />}
      </View>
      {/* The header is position: fixed on mobile; this keeps its 60px in the page flow (see modern.css). */}
      <div className="mp-site-header-spacer" aria-hidden="true" />
    </>
  )
}